import { useEffect, useRef, useState, type FormEvent } from "react";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { uploadToSignedUrl } from "../lib/verification";
import {
  listComplianceDocs, createComplianceDoc, deleteComplianceDoc, carrierPacketLink,
  DOC_TYPES, DOC_TYPE_LABELS, type ComplianceDoc, type DocType, type ExpiryStatus,
} from "../lib/compliance";

const STATUS_STYLE: Record<ExpiryStatus, { label: string; cls: string }> = {
  valid: { label: "Valid", cls: "text-verified" },
  expiring_soon: { label: "Expiring soon", cls: "text-[#c9a227]" },
  expired: { label: "Expired", cls: "text-danger" },
  no_expiry: { label: "No expiry", cls: "text-muted" },
};

export function Compliance() {
  const [docs, setDocs] = useState<ComplianceDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ doc_type: "insurance_coi" as DocType, title: "", issued_at: "", expires_at: "" });
  const [busy, setBusy] = useState(false);
  const [packet, setPacket] = useState<string | null>(null);
  const [packetUrl, setPacketUrl] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function refresh() { setDocs((await listComplianceDocs()).documents); }
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  async function add(e: FormEvent) {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const ext = (file.name.split(".").pop() ?? "pdf").toLowerCase();
      const { upload_url } = await createComplianceDoc({
        doc_type: form.doc_type, title: form.title || undefined,
        issued_at: form.issued_at || undefined, expires_at: form.expires_at || undefined, ext,
      });
      await uploadToSignedUrl(upload_url, file);
      setForm({ doc_type: "insurance_coi", title: "", issued_at: "", expires_at: "" });
      if (fileRef.current) fileRef.current.value = "";
      await refresh();
    } finally { setBusy(false); }
  }

  async function remove(id: string) {
    setBusy(true);
    try { await deleteComplianceDoc(id); await refresh(); } finally { setBusy(false); }
  }

  async function share() {
    const { path } = await carrierPacketLink();
    const url = `${window.location.origin}${path}`;
    setPacketUrl(url);
    try { await navigator.clipboard.writeText(url); setPacket("Carrier-packet link copied — share it with brokers."); }
    catch { setPacket("Carrier-packet link ready — copy it below."); }
  }

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <PageHeader
        title="Compliance vault"
        subtitle="Keep your insurance, authority, W-9, CDL, and medical docs current — and share a carrier packet with brokers."
        actions={<Button variant="ghost" onClick={share} data-testid="share-packet">Share carrier packet</Button>}
      />
      {packet && <p className="font-mono text-[11px] text-verified" data-testid="packet-msg">{packet}</p>}
      {packetUrl && <a href={packetUrl} target="_blank" rel="noreferrer" className="justify-self-start break-all font-mono text-[11px] text-accent-strong" data-testid="packet-url">{packetUrl}</a>}

      <Panel title="Add a document">
        <form className="grid gap-3 sm:grid-cols-2" onSubmit={add}>
          <select className={inputClass} value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value as DocType })} aria-label="Document type">
            {DOC_TYPES.map((t) => <option key={t} value={t}>{DOC_TYPE_LABELS[t]}</option>)}
          </select>
          <input className={inputClass} placeholder="Title (e.g. Progressive COI)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <label className="grid gap-1 text-[11px] text-muted">Issued<input type="date" className={inputClass} value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} /></label>
          <label className="grid gap-1 text-[11px] text-muted">Expires<input type="date" className={inputClass} value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /></label>
          <input ref={fileRef} type="file" accept="application/pdf,image/*" className="text-sm sm:col-span-2" aria-label="Document file" required />
          <Button type="submit" disabled={busy} className="sm:col-span-2">Upload document</Button>
        </form>
      </Panel>

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : docs.length === 0 ? (
        <p className="font-mono text-sm text-muted">No documents yet. Add your insurance, authority, and licenses.</p>
      ) : (
        <Panel title={`Documents (${docs.length})`}>
          <ul className="grid gap-2" data-testid="doc-list">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 border-b border-line pb-2 text-sm last:border-0 last:pb-0">
                <div className="grid gap-0.5">
                  <span className="font-bold">{d.title || DOC_TYPE_LABELS[d.doc_type]}</span>
                  <span className="font-mono text-[11px] text-muted">
                    {DOC_TYPE_LABELS[d.doc_type]}{d.expires_at ? ` · expires ${d.expires_at}` : ""}
                  </span>
                </div>
                <span className="flex items-center gap-3">
                  <span className={`font-mono text-[11px] font-bold ${STATUS_STYLE[d.status].cls}`}>{STATUS_STYLE[d.status].label}</span>
                  {d.view_url && <a href={d.view_url} target="_blank" rel="noreferrer" className="text-accent-strong">View</a>}
                  <button type="button" className="font-mono text-[11px] text-danger hover:underline" disabled={busy} onClick={() => remove(d.id)}>Delete</button>
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}
